import { config } from "../../config/config";
import { currentGymSchema, type CurrentGym } from "../../utils/zod/gym";
import {
  appCatalogResponseSchema,
  type AppCatalogEntryInput,
  type AppCatalogFilters,
  type AppCatalogResponse,
} from "../../utils/zod/gymAppCatalog";
import {
  clientAppConfigSchema,
  type ClientAppConfig,
} from "../../utils/zod/clientAppConfig";

/**
 * Returns the current gym for the authenticated user, or null when the user
 * has no associated gym (e.g. SUPERADMIN). 404 is treated as "no gym" and not thrown.
 */
export const getCurrentGymRequest = async (
  token: string,
): Promise<CurrentGym | null> => {
  const response = await fetch(`${config.API_URL}/gym/current`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    let errorMessage = "Error al obtener datos del negocio";
    if (contentType?.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
    }
    throw new Error(errorMessage);
  }

  return currentGymSchema.parse(await response.json());
};

export const updateGymLogoRequest = async (
  logoKey: string | null,
  token: string,
  logoKeyEmail?: string | null,
): Promise<{ logoKey: string | null; logoKeyEmail: string | null }> => {
  const response = await fetch(`${config.API_URL}/gym/current/logo`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ logoKey, logoKeyEmail: logoKeyEmail ?? null }),
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    let errorMessage = "Error al actualizar el logo del negocio";
    if (contentType?.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export type GatewayConnectionType = "oauth" | "manual";

export const MP_CONNECT_GENERIC_ERROR =
  "No pudimos conectar con Mercado Pago. Intenta más tarde.";

export interface ProviderConfigStatus {
  provider: string;
  configured: boolean;
  configuredAt: string | null;
  configuredBy: string | null;
  webhookUrl: string | null;
  /** Ausente en backends previos al onboarding OAuth: tratar como "manual". */
  connectionType?: GatewayConnectionType;
  mpUserId?: number;
}

export interface GatewayConfigResponse {
  providers: ProviderConfigStatus[];
}

export const getGymGatewayConfigRequest = async (
  token: string,
): Promise<GatewayConfigResponse> => {
  const response = await fetch(`${config.API_URL}/gym/gateway-config`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al obtener la configuracion del gateway",
    );
  }

  return response.json();
};

export const getMercadoPagoAuthorizeUrlRequest = async (
  token: string,
): Promise<{ url: string }> => {
  const response = await fetch(
    `${config.API_URL}/v1/gateway/oauth/mercadopago/authorize`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 503) {
      throw new Error("Pagos online no disponibles por ahora");
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || response.statusText || MP_CONNECT_GENERIC_ERROR,
    );
  }

  return response.json();
};

export const saveGymGatewayConfigRequest = async (
  provider: string,
  credentials: Record<string, string>,
  token: string,
): Promise<{ success: boolean }> => {
  const response = await fetch(
    `${config.API_URL}/gym/gateway-config/${provider}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(credentials),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al configurar el proveedor de pagos",
    );
  }

  return response.json();
};

export const deleteGymGatewayConfigRequest = async (
  provider: string,
  token: string,
): Promise<void> => {
  const response = await fetch(
    `${config.API_URL}/gym/gateway-config/${provider}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al eliminar la configuracion del gateway",
    );
  }
};

export const updateGymMailQuotaRequest = async (
  gymPublicId: string,
  emailDailyLimit: number | null,
  token: string,
): Promise<{ emailDailyLimit: number | null }> => {
  const response = await fetch(
    `${config.API_URL}/v1/admin/gyms/${gymPublicId}/mail-quota`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ emailDailyLimit }),
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      (body as { error?: string } | null)?.error ||
        "Error al actualizar el tope de correos",
    );
  }

  return response.json();
};

export const updateGymTaxesConfigRequest = async (
  gymPublicId: string,
  vat: number,
  token: string,
): Promise<{ vat: number }> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/taxes-config`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ vat }),
    },
  );

  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    let errorMessage = "Error al actualizar la configuración de impuestos";
    if (contentType?.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export interface DayOperatingHours {
  openHour: number;
  closeHour: number;
  closed: boolean;
}

export interface OperatingHoursResponse {
  operatingHours: DayOperatingHours[] | null;
}

export const getOperatingHoursRequest = async (
  token: string,
): Promise<OperatingHoursResponse> => {
  const response = await fetch(`${config.API_URL}/gym/operating-hours`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al obtener horarios de operación",
    );
  }
  return response.json();
};

export const updateOperatingHoursRequest = async (
  input: { operatingHours: DayOperatingHours[] },
  token: string,
): Promise<OperatingHoursResponse> => {
  const response = await fetch(`${config.API_URL}/gym/operating-hours`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al actualizar horarios de operación",
    );
  }
  return response.json();
};

// El mensaje de un ZodError es el array de issues serializado, y el hook lo
// muestra tal cual en un toast: mejor un texto legible.
const parseAppCatalogResponse = (raw: unknown): AppCatalogResponse => {
  const parsed = appCatalogResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error("El catálogo llegó con un formato inesperado");
  }
  return parsed.data;
};

const appCatalogQuery = (
  page: number,
  search: string,
  filters: AppCatalogFilters,
) => {
  const params = new URLSearchParams({ page: String(page) });
  if (search.trim()) params.set("search", search.trim());
  if (filters.visible !== undefined)
    params.set("visible", String(filters.visible));
  if (filters.mode) params.set("mode", filters.mode);
  if (filters.provider) params.set("provider", filters.provider);
  return params.toString();
};

export const getGymAppCatalogRequest = async (
  page: number,
  search: string,
  filters: AppCatalogFilters,
  token: string,
): Promise<AppCatalogResponse> => {
  const response = await fetch(
    `${config.API_URL}/v1/gym/app-catalog?${appCatalogQuery(page, search, filters)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al obtener el catálogo de la app",
    );
  }

  return parseAppCatalogResponse(await response.json());
};

/**
 * `availableDays` y `availableHours` se omiten cuando no llevan información
 * (estaban vacíos y siguen vacíos): el backend lo interpreta como "conservar lo
 * guardado", que en ese caso es lo mismo, y así un backend anterior a la
 * restricción, cuyo esquema es `.strict()`, no rechaza el guardado entero
 * durante un despliegue escalonado. Si el manager vació la ventana, el valor
 * vacío SÍ viaja: es lo único que distingue "borralo" de "no lo toqué".
 * `requiredTagPublicIds` no viaja nunca: las etiquetas se editan desde Planes y
 * el backend conserva lo guardado cuando falta.
 */
type OmittableKeys = "availableDays" | "availableHours";

export type AppCatalogEntryPayload = Omit<AppCatalogEntryInput, OmittableKeys> &
  Partial<Pick<AppCatalogEntryInput, OmittableKeys>>;

export const updateGymAppCatalogRequest = async (
  entries: AppCatalogEntryPayload[],
  page: number,
  search: string,
  filters: AppCatalogFilters,
  token: string,
): Promise<AppCatalogResponse> => {
  const response = await fetch(
    `${config.API_URL}/v1/gym/app-catalog?${appCatalogQuery(page, search, filters)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ entries }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al guardar el catálogo de la app",
    );
  }

  return parseAppCatalogResponse(await response.json());
};

export const getClientAppConfigRequest = async (
  token: string,
): Promise<ClientAppConfig> => {
  const response = await fetch(`${config.API_URL}/v1/gym/client-app-config`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al obtener la configuración de la app",
    );
  }

  return clientAppConfigSchema.parse(await response.json());
};

export const updateClientAppConfigRequest = async (
  body: ClientAppConfig,
  token: string,
): Promise<ClientAppConfig> => {
  const response = await fetch(`${config.API_URL}/v1/gym/client-app-config`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al guardar la configuración de la app",
    );
  }

  return clientAppConfigSchema.parse(await response.json());
};
